import { directive, Directive } from 'lit/directive.js';
import { LitElement, css } from 'lit';
import { state, query, property } from 'lit/decorators.js';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

const ARIA_IDREF_ATTRIBUTES = new Map([
    ['aria-activedescendant', 'ariaActivedescendantElement'],
    ['aria-controls', 'ariaControlsElements'],
    ['aria-describedby', 'ariaDescribedByElements'],
    ['aria-details', 'ariaDetailsElements'],
    ['aria-errormessage', 'ariaErrormessageElements'],
    ['aria-flowto', 'ariaFlowtoElements'],
    ['aria-labelledby', 'ariaLabelledByElements'],
    ['aria-owns', 'ariaOwnsElements'],
]);
function resolveAriaIdRefs(element, value) {
    const ids = value
        .trim()
        .split(/\s+/)
        .filter((id) => id.length > 0);
    if (ids.length === 0)
        return null;
    const root = element.getRootNode();
    let resolvedElements = [];
    if (root instanceof ShadowRoot) {
        resolvedElements = ids.map((id) => root.getElementById(id));
    }
    else if (root instanceof Document) {
        resolvedElements = ids.map((id) => root.getElementById(id));
    }
    else {
        resolvedElements = ids.map((id) => document.getElementById(id));
    }
    if (resolvedElements.filter((el) => el !== null).length === 0) {
        resolvedElements = ids.map((id) => document.getElementById(id));
    }
    const validElements = resolvedElements.filter((el) => el !== null);
    return validElements.length === ids.length ? validElements : null;
}
function setInternalsProperty(el, name, resolvedElements) {
    if (name === 'aria-activedescendant') {
        const [firstElement] = resolvedElements;
        // eslint-disable-next-line no-param-reassign
        el[name] = firstElement;
    }
    else {
        // eslint-disable-next-line no-param-reassign
        el[name] = resolvedElements;
    }
}
class SetAttributesDirective extends Directive {
    update(part, [attributes]) {
        const el = part.element;
        const internals = el.internals ?? null;
        for (const [name, value] of Object.entries(attributes)) {
            if (value == null) {
                el.removeAttribute(name);
            }
            else {
                const internalsPropertyName = ARIA_IDREF_ATTRIBUTES.get(name);
                if (internalsPropertyName) {
                    const resolvedElements = resolveAriaIdRefs(el, String(value));
                    if (resolvedElements) {
                        if (internals) {
                            // Set on ElementInternals
                            setInternalsProperty(internals, internalsPropertyName, resolvedElements);
                        }
                        else {
                            // Set internals property on shadowdom item
                            el.setAttribute(name, String(value));
                            setInternalsProperty(el, internalsPropertyName, resolvedElements);
                        }
                    }
                }
                else {
                    // Non-ARIA IDREF attributes: set normally
                    el.setAttribute(name, String(value));
                }
            }
        }
        return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    render(_attributes) {
        return null;
    }
}
/* How to use:

<textarea ${setAttributes(this._ariaAttributes)} ... />
*/
directive(SetAttributesDirective);

function r(e){var t,f,n="";if("string"==typeof e||"number"==typeof e)n+=e;else if("object"==typeof e)if(Array.isArray(e)){var o=e.length;for(t=0;t<o;t++)e[t]&&(f=r(e[t]))&&(n&&(n+=" "),n+=f);}else for(f in e)e[f]&&(n&&(n+=" "),n+=f);return n}function clsx(){for(var e,t,f=0,n="",o=arguments.length;f<o;f++)(e=arguments[f])&&(t=r(e))&&(n&&(n+=" "),n+=t);return n}

const connectedElements = new Set();
if (window && !window.translations) {
    window.translations = new Map();
}
const { translations } = window;
let fallback;
// TODO: We need some way for users to be able to set these on the server.
let documentDirection = 'ltr';
// Fallback for server.
let documentLanguage = 'en';
const isClient = typeof MutationObserver !== 'undefined' &&
    typeof document !== 'undefined' &&
    typeof document.documentElement !== 'undefined';
/** Updates all localized elements that are currently connected */
function update() {
    if (isClient) {
        documentDirection = document.documentElement.dir || 'ltr';
        documentLanguage = document.documentElement.lang || navigator.language;
    }
    [...connectedElements.keys()].forEach((el) => {
        const litEl = el;
        if (typeof litEl.requestUpdate === 'function') {
            litEl.requestUpdate();
        }
    });
}
if (isClient) {
    const documentElementObserver = new MutationObserver(update);
    documentDirection = document.documentElement.dir || 'ltr';
    documentLanguage = document.documentElement.lang || navigator.language;
    // Watch for changes on <html lang>
    documentElementObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['dir', 'lang'],
    });
}
/** Registers one or more translations */
function registerTranslation(...translation) {
    translation.forEach((t) => {
        const code = t.$code.toLowerCase();
        if (translations.has(code)) {
            // Merge translations that share the same language code
            translations.set(code, { ...translations.get(code), ...t });
        }
        else {
            translations.set(code, t);
        }
        // The first translation that's registered is the fallback
        if (!fallback) {
            fallback = t;
        }
    });
    update();
}
if (typeof window !== 'undefined') {
    window.registerTranslation = registerTranslation;
}
/**
 * Localize Reactive Controller for components built with Lit
 *
 * To use this controller, import the class and instantiate it in a custom element constructor:
 *
 * private localize = new LocalizeController(this);
 *
 * This will add the element to the set and make it respond to changes to <html dir|lang> automatically. To make it
 * respond to changes to its own dir|lang properties, make it a property:
 *
 *   @property() dir: string;
 *   @property() lang: string;
 *
 * To use a translation method, call it like this:
 *
 *   ${this.localize.term('term_key_here')}
 *   ${this.localize.date('2021-12-03')}
 *   ${this.localize.number(1000000)}
 */
class LocalizeController {
    constructor(host) {
        this.host = host;
        this.host.addController(this);
    }
    hostConnected() {
        connectedElements.add(this.host);
    }
    hostDisconnected() {
        connectedElements.delete(this.host);
    }
    /**
     * Gets the host element's directionality as determined by the `dir` attribute. The return value is transformed to
     * lowercase.
     */
    dir() {
        return `${this.host.dir || documentDirection}`.toLowerCase();
    }
    /**
     * Gets the host element's language as determined by the `lang` attribute. The return value is transformed to
     * lowercase.
     */
    lang() {
        return `${this.host.lang || documentLanguage}`.toLowerCase();
    }
    // eslint-disable-next-line class-methods-use-this
    getTranslationData(lang) {
        // Convert "en_US" to "en-US". Note that both underscores and dashes are allowed per spec, but underscores result in
        // a RangeError by the call to `new Intl.Locale()`. See: https://unicode.org/reports/tr35/#unicode-locale-identifier
        const locale = new Intl.Locale(lang.replace(/_/g, '-'));
        const language = locale?.language.toLowerCase();
        const region = locale?.region?.toLowerCase() ?? '';
        const primary = translations.get(`${language}-${region}`);
        const secondary = translations.get(language);
        return { locale, language, region, primary, secondary };
    }
    /** Determines if the specified term exists, optionally checking the fallback translation. */
    exists(key, options) {
        const { primary, secondary } = this.getTranslationData(options.lang ?? this.lang());
        const mergedOptions = {
            includeFallback: false,
            ...options,
        };
        if ((primary && primary[key]) ||
            (secondary && secondary[key]) ||
            (mergedOptions.includeFallback && fallback && fallback[key])) {
            return true;
        }
        return false;
    }
    /** Outputs a translated term. */
    term(key, ...args) {
        const { primary, secondary } = this.getTranslationData(this.lang());
        let term;
        // Look for a matching term using regionCode, code, then the fallback
        if (primary && primary[key]) {
            term = primary[key];
        }
        else if (secondary && secondary[key]) {
            term = secondary[key];
        }
        else if (fallback && fallback[key]) {
            term = fallback[key];
        }
        else {
            // eslint-disable-next-line no-console
            console.error(`No translation found for: ${String(key)}`);
            return String(key);
        }
        if (typeof term === 'function') {
            return term(...args);
        }
        return term;
    }
    /** Outputs a localized date in the specified format. */
    date(dateToFormat, options) {
        const date = new Date(dateToFormat);
        return new Intl.DateTimeFormat(this.lang(), options).format(date);
    }
    /** Outputs a localized number in the specified format. */
    number(numberToFormat, options) {
        const num = Number(numberToFormat);
        return Number.isNaN(num) ? '' : new Intl.NumberFormat(this.lang(), options).format(num);
    }
    /** Outputs a localized time in relative format. */
    relativeTime(value, unit, options) {
        return new Intl.RelativeTimeFormat(this.lang(), options).format(value, unit);
    }
}

/**
 * @param Base The base class.
 * @returns A mix-in implementing `localizations` method.
 *
 *@example
 *    <!-- Terms -->
      ${this.$localize.term('hello')}
      or
      ${this.$t('hello')}

      <!-- Dates -->
      ${this.$localize.date('2021-09-15 14:00:00 ET', { month: 'long', day: 'numeric', year: 'numeric' })}
      or
      ${this.$d('2021-09-15 14:00:00 ET', { month: 'long', day: 'numeric', year: 'numeric' })}

      <!-- Numbers/currency -->
      ${this.$localize.number(1000, { style: 'currency', currency: 'USD'})}
      or
      ${this.$n(1000,{ style: 'currency', currency: 'USD'})}

      <!-- Determining language -->
      ${this.$localize.lang()}

      <!-- Determining directionality, e.g. 'ltr' or 'rtl' -->
      ${this.$localize.dir()}


    *** HOW TO DEFINE TRANSLATIONS: ***
    // Simple terms
    upload: 'Upload',

    // Terms with placeholders
    greetUser: (name: string) => `Hello, ${name}!`,

    // Plurals
    numFilesSelected: (count: number) => {
        if (count === 0) return 'No files selected';
        if (count === 1) return '1 file selected';
        return `${count} files selected`;
    }
 */
const LocalizeMixin = (Base) => class extends Base {
    constructor() {
        super(...arguments);
        this.localize = new LocalizeController(this);
    }
    // Provide default values to avoid definite assignment errors and avoid decorators
    // commentati perchè danno problemi su React.js. Sono attributi nativi, e assegnare un valore di default a react da fastidio
    // dir: string = '';
    // lang: string = '';
    /**
     * Restituisce tutta l'utility di traduzione
     *

     *
     * @returns tutta l'utility di traduzione
     *
     * @example
     * this.$localize.lang() -> ritorna la lingua corrente
     * this.$localize.dir() -> ritorna la direzione della lingua corrente
     */
    get $localize() {
        return this.localize;
    }
    /**
     * Restituisce una stringa localizzata a partire da una chiave di termine.
     *
     * Utilizza il `LocalizeController` per accedere al dizionario corrente e
     * tradurre la chiave fornita secondo la lingua attiva.
     *
     * @param t - La chiave del termine da localizzare (es. 'hello', 'submit', ecc.).
     * @returns La stringa tradotta in base alla lingua attiva. Se la chiave non è trovata, restituisce la chiave stessa.
     *
     * @example
     * this.$t('hello'); // → "Ciao" (in locale it-IT)
     */
    $t(t) {
        // format term
        return this.localize.term(t);
    }
    /**
     * Formatta una data in base alla localizzazione attiva.
     *
     * Utilizza il `LocalizeController` per restituire una stringa localizzata
     * secondo le opzioni fornite (es. mese esteso, anno, ecc.).
     *
     * @param n - La data da formattare come stringa compatibile (es. ISO o con timezone, es. '2021-09-15 14:00:00 ET').
     * @param p - Le opzioni di formattazione per `Intl.DateTimeFormat` (es. { year: 'numeric', month: 'long', day: 'numeric' }).
     * @returns Una stringa rappresentante la data formattata secondo la localizzazione attiva.
     *
     * @example
     * this.$d('2021-09-15 14:00:00 ET', { year: 'numeric', month: 'long', day: 'numeric' });
     * // → "15 settembre 2021" (in locale it-IT)
     */
    $d(d, p) {
        // format date
        return this.localize.date(d, p);
    }
    /**
     * Formatta un numero secondo le impostazioni locali dell'utente corrente.
     *
     * Utilizza il `LocalizeController` per applicare formattazione numerica,
     * incluse opzioni come separatori, decimali, valute, ecc.
     *
     * @param d - Il numero da formattare.
     * @param p - Le opzioni di formattazione (es. { style: 'currency', currency: 'EUR' }).
     * @returns Una stringa rappresentante il numero formattato secondo la localizzazione attiva.
     *
     * @example
     * this.$n(1234.56, { style: 'currency', currency: 'USD' }); // → "$1,234.56" (in locale en-US)
     */
    $n(d, p) {
        return this.localize.number(d, p);
    }
};

/* eslint-disable no-console */
class Logger {
    constructor(tag) {
        this.tag = tag;
    }
    format(level, msg) {
        return [`[${this.tag}] ${msg}`];
    }
    warn(msg) {
        console.warn(...this.format('warn', msg));
    }
    error(msg) {
        console.error(...this.format('error', msg));
    }
    info(msg) {
        console.info(...this.format('info', msg));
    }
}

/**
 * Factory function per creare una base class estendibile
 * con stili personalizzati.
 */
class BaseComponent extends LitElement {
    constructor() {
        super();
        this.composeClass = clsx;
        this.logger = new Logger(this.tagName.toLowerCase());
    }
    get _ariaAttributes() {
        const attributes = {};
        for (const attr of this.getAttributeNames()) {
            if (attr === 'it-role') {
                attributes.role = this.getAttribute(attr);
            }
            else if (attr.startsWith('it-aria-')) {
                attributes[attr.replace(/^it-/, '')] = this.getAttribute(attr);
            }
        }
        return attributes;
    }
    // eslint-disable-next-line class-methods-use-this
    generateId(prefix) {
        return `${prefix}-${Math.random().toString(36).slice(2)}`;
    }
    // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-unused-vars
    addFocus(element) {
        // new TrackFocus(element); // per il momento è stato disattivato perchè ci sono le pseudo classi ::focus-visible per fare quello che fa TrackFocus. Si possono aggiungere regole css in bsi-italia 3 dato che stiamo facendo una breaking release di bsi.
    }
    // eslint-disable-next-line class-methods-use-this
    getActiveElement() {
        let active = document.activeElement;
        while (active && active.shadowRoot && active.shadowRoot.activeElement) {
            active = active.shadowRoot.activeElement;
        }
        return active;
    }
    get focusElement() {
        return this;
    }
    // eslint-disable-next-line class-methods-use-this
    get prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    connectedCallback() {
        super.connectedCallback();
        // generate internal _id
        const prefix = this.id?.length > 0 ? this.id : this.tagName.toLowerCase();
        this._id = this.generateId(prefix);
        this.ariaAttributesObserver = new MutationObserver((mutations) => {
            const shouldUpdate = mutations.some((mutation) => {
                if (mutation.type !== 'attributes' || !mutation.attributeName) {
                    return false;
                }
                return mutation.attributeName === 'it-role' || mutation.attributeName.startsWith('it-aria-');
            });
            if (shouldUpdate) {
                this.requestUpdate();
            }
        });
        this.ariaAttributesObserver.observe(this, {
            attributes: true,
            attributeOldValue: true,
        });
    }
    disconnectedCallback() {
        this.ariaAttributesObserver?.disconnect();
        this.ariaAttributesObserver = undefined;
        super.disconnectedCallback();
    }
}
const BaseLocalizedComponent = LocalizeMixin(BaseComponent);

//
// We store a WeakMap of forms + controls so we can keep references to all FormControl within a given form. As
// elements connect and disconnect to/from the DOM, their containing form is used as the key and the form control is
// added and removed from the form's set, respectively.
//
const formCollections = new WeakMap();
//
// We store a WeakMap of reportValidity() overloads so we can override it when form controls connect to the DOM and
// restore the original behavior when they disconnect.
//
const reportValidityOverloads = new WeakMap();
const checkValidityOverloads = new WeakMap();
//
// We store a Set of controls that users have interacted with. This allows us to determine the interaction state
// without littering the DOM with additional data attributes.
//
const userInteractedControls = new WeakSet();
//
// We store a WeakMap of interactions for each form control so we can track when all conditions are met for validation.
//
const interactions = new WeakMap();
/** A reactive controller to allow form controls to participate in form submission, validation, etc. */
class FormControlController {
    constructor(host, options) {
        this.submittedOnce = false;
        this.handleFormData = (event) => {
            // console.log('handleFormData');
            const disabled = this.options.disabled(this.host);
            const name = this.options.name(this.host);
            const value = this.options.value(this.host);
            const tagName = this.host.tagName.toLowerCase();
            // For buttons, we only submit the value if they were the submitter. This is currently done in doAction() by
            // injecting the name/value on a temporary button, so we can just skip them here.
            const isButton = tagName === 'it-button';
            if (this.host.isConnected &&
                !disabled &&
                !isButton &&
                typeof name === 'string' &&
                name.length > 0 &&
                typeof value !== 'undefined') {
                switch (tagName) {
                    case 'it-radio':
                        if (this.host.checked) {
                            event.formData.append(name, value);
                        }
                        break;
                    case 'it-checkbox':
                    case 'it-toggle':
                        if (this.host.checked) {
                            if (event.formData.getAll(name).indexOf(value) < 0) {
                                // handle group checkbox
                                event.formData.append(name, value);
                            }
                        }
                        break;
                    case 'it-checkbox-group':
                    case 'it-toggle-group':
                        // non settare valori in formData, perchè ogni singola checkbox setta il suo valore
                        break;
                    case 'it-upload':
                        // value is File[] — append each File object directly (not as string)
                        if (Array.isArray(value)) {
                            value.forEach((file) => {
                                event.formData.append(name, file);
                            });
                        }
                        break;
                    default:
                        if (Array.isArray(value)) {
                            value.forEach((val) => {
                                event.formData.append(name, val.toString());
                            });
                        }
                        else {
                            event.formData.append(name, value.toString());
                        }
                }
            }
        };
        this.handleFormSubmit = (event) => {
            const disabled = this.options.disabled(this.host);
            const reportValidity = this.options.reportValidity;
            // Update the interacted state for all controls when the form is submitted
            if (this.form && !this.form.noValidate) {
                formCollections.get(this.form)?.forEach((control) => {
                    this.setUserInteracted(control, true);
                });
            }
            const resReportValidity = reportValidity(this.host);
            if (this.form && !this.form.noValidate && !disabled && !resReportValidity) {
                event.preventDefault();
                // event.stopImmediatePropagation(); // se lo attiviamo, valida un campo alla volta
                // Scroll al primo controllo non valido
                const formControls = formCollections.get(this.form);
                if (formControls) {
                    for (const control of formControls) {
                        if (!control.validity?.valid) {
                            // Scroll smooth verso il controllo non valido
                            control.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center',
                            });
                            break;
                        }
                    }
                }
            }
            this.submittedOnce = true;
        };
        this.handleFormReset = () => {
            this.options.setValue(this.host, '');
            this.submittedOnce = false;
            this.setUserInteracted(this.host, false);
            interactions.set(this.host, []);
        };
        this.handleInteraction = (event) => {
            const emittedEvents = interactions.get(this.host);
            if (!emittedEvents.includes(event.type)) {
                emittedEvents.push(event.type);
            }
            // Mark it as user-interacted as soon as all associated events have been emitted
            if (emittedEvents.length === this.options.assumeInteractionOn.length) {
                this.setUserInteracted(this.host, true);
            }
        };
        this.checkFormValidity = () => {
            // console.log('checkFormValidity');
            //
            // This is very similar to the `reportFormValidity` function, but it does not trigger native constraint validation
            // Allow the user to simply check if the form is valid and handling validity in their own way.
            //
            // We preserve the original method in a WeakMap, but we don't call it from the overload because that would trigger
            // validations in an unexpected order. When the element disconnects, we revert to the original behavior. This won't
            // be necessary once we can use ElementInternals.
            //
            // Note that we're also honoring the form's novalidate attribute.
            //
            if (this.form && !this.form.noValidate) {
                // This seems sloppy, but checking all elements will cover native inputs, Shoelace inputs, and other custom
                // elements that support the constraint validation API.
                const elements = this.form.querySelectorAll('*');
                for (const element of elements) {
                    if (typeof element.checkValidity === 'function') {
                        if (!element.checkValidity()) {
                            return false;
                        }
                    }
                }
            }
            return true;
        };
        this.reportFormValidity = () => {
            // console.log('reportFormValidity');
            //
            // FormControl work hard to act like regular form controls. They support the Constraint Validation API
            // and its associated methods such as setCustomValidity() and reportValidity(). However, the HTMLFormElement also
            // has a reportValidity() method that will trigger validation on all child controls. Since we're not yet using
            // ElementInternals, we need to overload this method so it looks for any element with the reportValidity() method.
            //
            // We preserve the original method in a WeakMap, but we don't call it from the overload because that would trigger
            // validations in an unexpected order. When the element disconnects, we revert to the original behavior. This won't
            // be necessary once we can use ElementInternals.
            //
            // Note that we're also honoring the form's novalidate attribute.
            //
            if (this.form && !this.form.noValidate) {
                // This seems sloppy, but checking all elements will cover native inputs, Shoelace inputs, and other custom
                // elements that support the constraint validation API.
                const elements = this.form.querySelectorAll('*');
                for (const element of elements) {
                    if (typeof element.reportValidity === 'function') {
                        if (!element.reportValidity()) {
                            return false;
                        }
                    }
                }
            }
            return true;
        };
        (this.host = host).addController(this);
        this.options = {
            form: (input) => {
                // If there's a form attribute, use it to find the target form by id
                // Controls may not always reflect the 'form' property. For example, `<it-button>` doesn't reflect.
                const formId = input.form;
                if (formId) {
                    const root = input.getRootNode();
                    const form = root.querySelector(`#${formId}`);
                    if (form) {
                        return form;
                    }
                }
                return input.closest('form');
            },
            name: (input) => input.name,
            value: (input) => input.value,
            disabled: (input) => input.disabled ?? false,
            reportValidity: (input) => typeof input.reportValidity === 'function' ? input.reportValidity() : true,
            checkValidity: (input) => (typeof input.checkValidity === 'function' ? input.checkValidity() : true),
            setValue: (input, value) => {
                // eslint-disable-next-line no-param-reassign
                input.value = value;
            },
            assumeInteractionOn: ['it-input'],
            ...options,
        };
    }
    hostConnected() {
        const form = this.options.form(this.host);
        if (form) {
            this.attachForm(form);
        }
        // Listen for interactions
        interactions.set(this.host, []);
        this.options.assumeInteractionOn.forEach((event) => {
            this.host.addEventListener(event, this.handleInteraction);
        });
    }
    hostDisconnected() {
        this.detachForm();
        // Clean up interactions
        interactions.delete(this.host);
        this.options.assumeInteractionOn.forEach((event) => {
            this.host.removeEventListener(event, this.handleInteraction);
        });
    }
    hostUpdated() {
        const form = this.options.form(this.host);
        // Detach if the form no longer exists
        if (!form) {
            this.detachForm();
        }
        // If the form has changed, reattach it
        if (form && this.form !== form) {
            this.detachForm();
            this.attachForm(form);
        }
        if (this.host.hasUpdated) {
            this.setValidity(this.host.validity.valid);
        }
    }
    attachForm(form) {
        if (form) {
            this.form = form;
            // Add this element to the form's collection
            if (formCollections.has(this.form)) {
                formCollections.get(this.form).add(this.host);
            }
            else {
                formCollections.set(this.form, new Set([this.host]));
            }
            this.form.addEventListener('formdata', this.handleFormData);
            this.form.addEventListener('submit', this.handleFormSubmit);
            this.form.addEventListener('reset', this.handleFormReset);
            // Overload the form's reportValidity() method so it looks at FormControl
            if (!reportValidityOverloads.has(this.form)) {
                reportValidityOverloads.set(this.form, this.form.reportValidity);
                this.form.reportValidity = () => this.reportFormValidity();
            }
            // Overload the form's checkValidity() method so it looks at FormControl
            if (!checkValidityOverloads.has(this.form)) {
                checkValidityOverloads.set(this.form, this.form.checkValidity);
                this.form.checkValidity = () => this.checkFormValidity();
            }
        }
        else {
            this.form = undefined;
        }
    }
    detachForm() {
        if (!this.form)
            return;
        const formCollection = formCollections.get(this.form);
        if (!formCollection) {
            return;
        }
        this.submittedOnce = false;
        // Remove this host from the form's collection
        formCollection.delete(this.host);
        // Check to make sure there's no other form controls in the collection. If we do this
        // without checking if any other controls are still in the collection, then we will wipe out the
        // validity checks for all other elements.
        // see: https://github.com/shoelace-style/shoelace/issues/1703
        if (formCollection.size <= 0) {
            this.form.removeEventListener('formdata', this.handleFormData);
            this.form.removeEventListener('submit', this.handleFormSubmit);
            this.form.removeEventListener('reset', this.handleFormReset);
            // Remove the overload and restore the original method
            if (reportValidityOverloads.has(this.form)) {
                this.form.reportValidity = reportValidityOverloads.get(this.form);
                reportValidityOverloads.delete(this.form);
            }
            if (checkValidityOverloads.has(this.form)) {
                this.form.checkValidity = checkValidityOverloads.get(this.form);
                checkValidityOverloads.delete(this.form);
            }
            // So it looks weird here to not always set the form to undefined. But I _think_ if we unattach this.form here,
            // we end up in this fun spot where future validity checks don't have a reference to the form validity handler.
            // First form element in sets the validity handler. So we can't clean up `this.form` until there are no other form elements in the form.
            this.form = undefined;
        }
    }
    // eslint-disable-next-line class-methods-use-this
    setUserInteracted(el, hasInteracted) {
        if (hasInteracted) {
            userInteractedControls.add(el);
        }
        else {
            userInteractedControls.delete(el);
        }
        el.requestUpdate();
    }
    doAction(type, submitter) {
        // console.log('doaction', type);
        if (this.form) {
            const button = document.createElement('button');
            button.type = type;
            button.style.position = 'absolute';
            button.style.width = '0';
            button.style.height = '0';
            button.style.clipPath = 'inset(50%)';
            button.style.overflow = 'hidden';
            button.style.whiteSpace = 'nowrap';
            // Pass name, value, and form attributes through to the temporary button
            if (submitter) {
                button.name = submitter.name;
                button.value = submitter.value;
                ['formaction', 'formenctype', 'formmethod', 'formnovalidate', 'formtarget'].forEach((attr) => {
                    if (submitter.hasAttribute(attr)) {
                        button.setAttribute(attr, submitter.getAttribute(attr));
                    }
                });
            }
            this.form.append(button);
            button.click();
            button.remove();
        }
    }
    /** Returns the associated `<form>` element, if one exists. */
    getForm() {
        return this.form ?? null;
    }
    /** Resets the form, restoring all the control to their default value */
    reset(submitter) {
        this.doAction('reset', submitter);
    }
    /** Submits the form, triggering validation and form data injection. */
    submit(submitter) {
        // Calling form.submit() bypasses the submit event and constraint validation. To prevent this, we can inject a
        // native submit button into the form, "click" it, then remove it to simulate a standard form submission.
        this.doAction('submit', submitter);
    }
    /**
     * Synchronously sets the form control's validity. Call this when you know the future validity but need to update
     * the host element immediately, i.e. before Lit updates the component in the next update.
     */
    setValidity(isValid) {
        const host = this.host;
        const hasInteracted = Boolean(userInteractedControls.has(host));
        const required = Boolean(host.required);
        //
        // We're mapping the following "states" to data attributes. In the future, we can use ElementInternals.states to
        // create a similar mapping, but instead of [data-invalid] it will look like :--invalid.
        //
        // See this RFC for more details: https://github.com/shoelace-style/shoelace/issues/1011
        //
        host.toggleAttribute('data-required', required);
        host.toggleAttribute('data-optional', !required);
        host.toggleAttribute('data-invalid', !isValid);
        host.toggleAttribute('data-valid', isValid);
        host.toggleAttribute('data-user-invalid', !isValid && hasInteracted);
        host.toggleAttribute('data-user-valid', isValid && hasInteracted);
    }
    userInteracted() {
        const hasInteracted = Boolean(userInteractedControls.has(this.host));
        return hasInteracted;
    }
    /**
     * Updates the form control's validity based on the current value of `host.validity.valid`. Call this when anything
     * that affects constraint validation changes so the component receives the correct validity states.
     */
    updateValidity() {
        const host = this.host;
        this.setValidity(host.validity.valid);
    }
    /**
     * Dispatches a non-bubbling, cancelable custom event of type `it-invalid`.
     * If the `it-invalid` event will be cancelled then the original `invalid`
     * event (which may have been passed as argument) will also be cancelled.
     * If no original `invalid` event has been passed then the `it-invalid`
     * event will be cancelled before being dispatched.
     */
    emitInvalidEvent(originalInvalidEvent) {
        const itInvalidEvent = new CustomEvent('it-invalid', {
            bubbles: false,
            composed: false,
            cancelable: true,
            detail: {},
        });
        if (!originalInvalidEvent) {
            itInvalidEvent.preventDefault();
        }
        if (!this.host.dispatchEvent(itInvalidEvent)) {
            originalInvalidEvent?.preventDefault();
        }
    }
}

const translation = {
    $code: 'it',
    $name: 'Italiano',
    $dir: 'ltr',
    validityRequired: 'Questo campo è obbligatorio.',
    validityGroupRequired: "Scegli almeno un'opzione",
    validityPattern: 'Il valore non corrisponde al formato richiesto.',
    validityMinlength: 'Il valore deve essere lungo almeno {minlength} caratteri.',
    validityMaxlength: 'Il valore deve essere lungo al massimo {maxlength} caratteri.',
};

registerTranslation(translation);
class FormControl extends BaseLocalizedComponent {
    constructor() {
        super(...arguments);
        this.formControlController = new FormControlController(this, {
            assumeInteractionOn: ['it-input', 'it-blur', 'it-change'],
        });
        // TODO: verificare se serve davvero con il fatto che usiamo form-controller
        // static formAssociated = true;
        // @property()
        // internals = this.attachInternals();
        this._touched = false;
        /** The name of the input, submitted as a name/value pair with form data. */
        this.name = '';
        /** The current value of the input, submitted as a name/value pair with form data. */
        this.value = '';
        /** If the input is disabled. */
        this.disabled = false;
        /**
         * By default, form controls are associated with the nearest containing `<form>` element. This attribute allows you
         * to place the form control outside of a form and associate it with the form that has this `id`. The form must be in
         * the same document or shadow root for this to work.
         */
        this.form = '';
        /** If you implement your custom validation and you won't to trigger default validation */
        this.customValidation = false;
        /** If your input is invalid from your custom validition, set this attribute with message validation */
        this.validationText = '';
        /**
         * Specifies the granularity that the value must adhere to, or the special value `any` which means no stepping is
         * implied, allowing any numeric value. Only applies to date and number input types.
         */
        this.step = 'any';
        /** The input's minimum length. */
        this.minlength = -1;
        /** The input's maximum length. */
        this.maxlength = -1;
        /** If the input is required. */
        this.required = false;
        /* For grouped input, like checkbox-group */
        this.isInGroup = false;
        this.validationMessage = '';
    }
    /** Gets the validity state object */
    get validity() {
        return this.inputElement?.validity;
    }
    /** Gets the associated form, if one exists. */
    getForm() {
        return this.formControlController.getForm();
    }
    // Form validation methods
    checkValidity() {
        const inputValid = this.inputElement?.checkValidity() ?? true; // this.inputElement.checkValidity() è la validazione del browser
        return inputValid;
    }
    /** Checks for validity and shows the browser's validation message if the control is invalid. */
    reportValidity() {
        // const ret = this.inputElement.checkValidity();
        const ret = this.checkValidity(); // chiama la checkValidity, che se è stata overridata chiama quella
        this.handleValidationMessages();
        return ret; // this.inputElement.reportValidity();
    }
    /** Sets a custom validation message. Pass an empty string to restore validity. */
    setCustomValidity(message) {
        this.inputElement.setCustomValidity(message);
        this.validationMessage = this.inputElement.validationMessage;
        this.formControlController.updateValidity();
    }
    // Handlers
    _handleReady() {
        requestAnimationFrame(() => {
            this.dispatchEvent(new CustomEvent('it-input-ready', { bubbles: true, detail: { el: this.inputElement } }));
        });
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleInput(e) {
        this.handleValidationMessages();
        this.dispatchEvent(new CustomEvent('it-input', {
            detail: { value: this.inputElement.value, el: this.inputElement },
            bubbles: true,
            composed: true,
        }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleBlur(e) {
        this._touched = true;
        this.handleValidationMessages();
        this.dispatchEvent(new FocusEvent('it-blur', { bubbles: true, composed: true }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleFocus(e) {
        this.dispatchEvent(new FocusEvent('it-focus', { bubbles: true, composed: true }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleClick(e) {
        this.dispatchEvent(new MouseEvent('it-click', { bubbles: true, composed: true }));
    }
    /*
    Override default browser validation messages
     */
    handleValidationMessages() {
        if (!this.customValidation) {
            const _v = this.inputElement.validity;
            const isRequiredHandledByGroup = this.isInGroup === true;
            if (_v.valueMissing && !isRequiredHandledByGroup) {
                this.setCustomValidity(this.$t('validityRequired'));
            }
            else if (_v.patternMismatch) {
                this.setCustomValidity(this.$t('validityPattern'));
            }
            else if (_v.tooShort) {
                this.setCustomValidity(this.$t('validityMinlength').replace('{minlength}', this.minlength.toString()));
            }
            else if (_v.tooLong) {
                this.setCustomValidity(this.$t('validityMaxlength').replace('{maxlength}', this.maxlength.toString()));
            }
            else {
                /* nothing. Usa il messaggio di errore della validazione
                di default del browser per altri errori di validità come:
                - typeMismatch
                - rangeUnderflow
                - rangeOverflow
                - stepMismatch
                - badInput */
                const otherConstraintErrors = _v.typeMismatch || _v.rangeUnderflow || _v.rangeOverflow || _v.stepMismatch || _v.badInput;
                if (!otherConstraintErrors) {
                    this.setCustomValidity('');
                }
            }
        }
        this.validationMessage = this.inputElement.validationMessage;
    }
    _handleInvalid(event) {
        this.formControlController.setValidity(false);
        this.formControlController.emitInvalidEvent(event);
    }
    _handleChange(e) {
        const target = e.target;
        let value;
        if (target instanceof HTMLInputElement) {
            switch (target.type) {
                case 'checkbox':
                case 'radio':
                    value = target.checked;
                    break;
                case 'file':
                    value = target.files; // FileList
                    break;
                default:
                    value = target.value;
            }
        }
        else if (target instanceof HTMLSelectElement) {
            if (target.multiple) {
                value = Array.from(target.selectedOptions).map((o) => o.value);
            }
            else {
                value = target.value;
            }
        }
        else {
            // textarea o altri input con value
            value = target.value;
        }
        this.dispatchEvent(new CustomEvent('it-change', {
            detail: { value, el: target },
            bubbles: true,
            composed: true,
        }));
    }
    updated(changedProperties) {
        super.updated?.(changedProperties);
        if (this.customValidation) {
            this.setCustomValidity(this.validationText ?? '');
        }
        else if (this.formControlController.userInteracted()) {
            this.formControlController.updateValidity();
        }
    }
}
__decorate([
    state(),
    __metadata("design:type", Object)
], FormControl.prototype, "_touched", void 0);
__decorate([
    query('.it-form__control'),
    __metadata("design:type", HTMLInputElement)
], FormControl.prototype, "inputElement", void 0);
__decorate([
    property({ type: String, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "name", void 0);
__decorate([
    property({ reflect: true }),
    __metadata("design:type", Object)
], FormControl.prototype, "value", void 0);
__decorate([
    property({ type: Boolean, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "disabled", void 0);
__decorate([
    property({ reflect: true, type: String }),
    __metadata("design:type", Object)
], FormControl.prototype, "form", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'custom-validation' }),
    __metadata("design:type", Object)
], FormControl.prototype, "customValidation", void 0);
__decorate([
    property({ attribute: 'validity-message', reflect: true }),
    __metadata("design:type", String)
], FormControl.prototype, "validationText", void 0);
__decorate([
    property(),
    __metadata("design:type", String)
], FormControl.prototype, "pattern", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "min", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "max", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "step", void 0);
__decorate([
    property({ type: Number }),
    __metadata("design:type", Object)
], FormControl.prototype, "minlength", void 0);
__decorate([
    property({ type: Number }),
    __metadata("design:type", Object)
], FormControl.prototype, "maxlength", void 0);
__decorate([
    property({ type: Boolean, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "required", void 0);
__decorate([
    property({ type: Boolean }),
    __metadata("design:type", Object)
], FormControl.prototype, "isInGroup", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], FormControl.prototype, "validationMessage", void 0);

if (typeof window !== 'undefined') {
    window._itAnalytics = window._itAnalytics || {};
    window._itAnalytics = {
        ...window._itAnalytics,
        version: '1.0.0-beta.3',
    };
}

var styles = css`@keyframes splide-loading {
  0% {
    transform: rotateZ(0);
  }
  100% {
    transform: rotateZ(360deg);
  }
}
.splide__track--draggable {
  -webkit-touch-callout: none;
  user-select: none;
}

.splide__track--fade > .splide__list > .splide__slide {
  margin: 0 !important;
  opacity: 0;
  z-index: 0;
}
.splide__track--fade > .splide__list > .splide__slide.is-active {
  opacity: 1;
  z-index: 1;
}

.splide--rtl {
  direction: rtl;
}

.splide__track--ttb > .splide__list {
  display: block;
}

.splide__container {
  box-sizing: border-box;
  position: relative;
}

.splide__list {
  backface-visibility: hidden;
  display: flex;
  height: 100%;
  margin: 0 !important;
  padding: 0 !important;
}
.splide.is-initialized:not(.is-active) .splide__list {
  display: block;
}

.splide__pagination {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: 0;
  pointer-events: none;
}
.splide__pagination li {
  display: inline-block;
  line-height: 1;
  list-style-type: none;
  margin: 0;
  pointer-events: auto;
}
.splide:not(.is-overflow) .splide__pagination {
  display: none;
}

.splide__progress__bar {
  width: 0;
}

.splide {
  position: relative;
  visibility: hidden;
}
.splide.is-initialized, .splide.is-rendered {
  visibility: visible;
}

.splide__slide {
  backface-visibility: hidden;
  box-sizing: border-box;
  flex-shrink: 0;
  list-style-type: none !important;
  margin: 0;
  position: relative;
}
.splide__slide img {
  vertical-align: bottom;
}

.splide__spinner {
  animation: splide-loading 1s infinite linear;
  border: 2px solid #999;
  border-left-color: transparent;
  border-radius: 50%;
  bottom: 0;
  contain: strict;
  display: inline-block;
  height: 20px;
  left: 0;
  margin: auto;
  position: absolute;
  right: 0;
  top: 0;
  width: 20px;
}

.splide__sr {
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
}

.splide__toggle__pause {
  display: none;
}
.splide__toggle.is-active .splide__toggle__play {
  display: none;
}
.splide__toggle.is-active .splide__toggle__pause {
  display: inline;
}

.splide__track {
  overflow: hidden;
  position: relative;
  z-index: 0;
}

.it-carousel-wrapper {
  --bsi-carousel-arrow-size: var(--bsi-spacing-xl);
  --bsi-carousel-arrow-icon-size: var(--bsi-spacing-s);
  --bsi-carousel-arrow-background: var(--bsi-color-background-primary);
  --bsi-carousel-arrow-icon-color: var(--bsi-color-text-inverse);
  --bsi-carousel-arrow-offset: var(--bsi-spacing-m);
  --bsi-carousel-card-padding-bottom: var(--bsi-spacing-s);
  --bsi-carousel-divider-color: var(--bsi-color-border-subtle);
  --bsi-carousel-divider-offset: var(--bsi-spacing-xs);
  --bsi-carousel-heading-font-size: 1.75rem;
  --bsi-carousel-heading-padding-inline: var(--bsi-spacing-m);
  --bsi-carousel-heading-border-color: var(--bsi-color-border-subtle);
  --bsi-carousel-landscape-card-spacing: var(--bsi-spacing-xxl);
  --bsi-carousel-pagination-dot-size: 1rem;
  --bsi-carousel-pagination-dot-color: var(--bsi-color-background-subtle);
  --bsi-carousel-pagination-spacing: var(--bsi-spacing-m);
  --bsi-carousel-pagination-height: var(--bsi-carousel-pagination-dot-size);
  --bsi-carousel-track-padding-top: var(--bsi-spacing-m);
}

/* stylelint-disable-next-line no-duplicate-selectors */
.it-carousel-wrapper {
  margin: 0 auto;
}
.it-carousel-wrapper .splide__pagination {
  margin-top: var(--bsi-carousel-pagination-spacing);
  margin-left: 0;
  padding-left: 0;
}
.it-carousel-wrapper .splide__pagination li:not(:last-child) {
  margin-right: var(--bsi-spacing-s);
}
.it-carousel-wrapper .splide__pagination button {
  display: inline-block;
  width: var(--bsi-carousel-pagination-dot-size);
  height: var(--bsi-carousel-pagination-dot-size);
  padding: 0;
  border: none;
  border-radius: 50px;
  background: var(--bsi-carousel-pagination-dot-color);
  box-shadow: none;
  transition: all 0.3s;
}
.it-carousel-wrapper .splide__pagination button.is-active {
  --bsi-carousel-pagination-dot-color: var(--bsi-color-background-primary);
}
.it-carousel-wrapper .it-special-card h5.card-title {
  margin-bottom: 0;
}
.it-carousel-wrapper .it-header-block h2 {
  margin-bottom: 0;
  padding-bottom: var(--bsi-carousel-pagination-spacing);
  border-bottom: 1px solid var(--bsi-carousel-heading-border-color);
}
.it-carousel-wrapper .splide__track {
  padding-top: var(--bsi-carousel-track-padding-top);
  padding-bottom: 0;
}
.it-carousel-wrapper .card-wrapper {
  padding-bottom: var(--bsi-carousel-card-padding-bottom);
}
.it-carousel-wrapper .it-card {
  padding-bottom: var(--bsi-carousel-card-padding-bottom);
}
.it-carousel-wrapper.it-carousel-landscape-abstract .it-single-slide-wrapper > a {
  position: relative;
  display: block;
}
.it-carousel-wrapper.it-carousel-landscape-abstract .it-single-slide-wrapper .it-text-slider-wrapper-outside .card {
  margin-right: 0;
  border-right: none;
}
.it-carousel-wrapper.it-carousel-landscape-abstract .it-single-slide-wrapper .it-text-slider-wrapper-outside .card:after {
  display: none;
}
.it-carousel-wrapper.it-carousel-landscape-abstract .it-single-slide-wrapper .it-text-slider-wrapper-outside .card .card-body .card-signature {
  width: 60%;
  margin-bottom: 0;
  float: left;
}
.it-carousel-wrapper.it-carousel-landscape-abstract .it-single-slide-wrapper .it-text-slider-wrapper-outside .card .card-body a.read-more {
  position: inherit;
  bottom: inherit;
  justify-content: flex-end;
  width: 35%;
  margin-bottom: 0;
  float: right;
  line-height: 21px;
}
.it-carousel-wrapper.it-carousel-landscape-abstract-three-cols-arrow-visible .splide__arrows {
  position: absolute;
  top: calc(50% - var(--bsi-carousel-pagination-height));
  right: calc(-1 * var(--bsi-carousel-arrow-offset));
  left: calc(-1 * var(--bsi-carousel-arrow-offset));
  z-index: 1;
  display: flex;
  justify-content: space-between;
  pointer-events: none;
}
.it-carousel-wrapper.it-carousel-landscape-abstract-three-cols-arrow-visible .splide__arrows .splide__arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--bsi-carousel-arrow-size);
  height: var(--bsi-carousel-arrow-size);
  border: none;
  background: var(--bsi-carousel-arrow-background);
  pointer-events: all;
}
.it-carousel-wrapper.it-carousel-landscape-abstract-three-cols-arrow-visible .splide__arrows .splide__arrow svg {
  width: var(--bsi-carousel-arrow-icon-size);
  height: var(--bsi-carousel-arrow-icon-size);
  fill: var(--bsi-carousel-arrow-icon-color);
}
.it-carousel-wrapper.it-carousel-landscape-abstract-three-cols-arrow-visible .splide__arrows .splide__arrow.splide__arrow--prev {
  transform: rotate(180deg);
}
.it-carousel-wrapper.it-carousel-landscape-abstract-three-cols-arrow-visible .splide__arrows .splide__arrow[disabled] {
  --bsi-carousel-arrow-background: var(--bsi-color-background-disabled);
}
.it-carousel-wrapper.it-carousel-landscape-abstract-3 {
  margin: 0 auto;
}
.it-carousel-wrapper.it-carousel-landscape-abstract-3 .it-header-block {
  padding: 0 var(--bsi-carousel-heading-padding-inline);
}
.it-carousel-wrapper.it-carousel-landscape-abstract-3 .card.card-img {
  margin-right: 0;
  margin-left: 0;
}
.it-carousel-wrapper.it-carousel-landscape-abstract-3 .it-card-bg .card.card-bg {
  margin-right: 0;
  margin-left: 0;
}
.it-carousel-wrapper.it-calendar-wrapper .splide__track {
  padding-top: 0;
}
.it-carousel-wrapper.it-calendar-wrapper .splide__track .card.card-bg {
  margin-right: 0;
  margin-left: 0;
}

@media (min-width: 992px) {
  .it-carousel-wrapper {
    --bsi-carousel-heading-padding-inline: var(--bsi-spacing-xs);
    --bsi-carousel-arrow-offset: var(--bsi-spacing-xxl);
  }
  .it-carousel-wrapper.it-carousel-landscape-abstract {
    max-width: 960px;
  }
  .it-carousel-wrapper.it-carousel-landscape-abstract .splide__list {
    display: flex;
  }
  .it-carousel-wrapper.it-carousel-landscape-abstract .splide__list .splide__slide {
    display: flex;
  }
  .it-carousel-wrapper.it-carousel-landscape-abstract .splide__list .splide__slide .it-single-slide-wrapper {
    height: 100%;
  }
  .it-carousel-wrapper.it-carousel-landscape-abstract .it-single-slide-wrapper {
    display: flex;
    flex-basis: 100%;
    width: 100%;
  }
  .it-carousel-wrapper.it-carousel-landscape-abstract .it-single-slide-wrapper .img-responsive-wrapper {
    flex-basis: 50%;
  }
  .it-carousel-wrapper.it-carousel-landscape-abstract .it-single-slide-wrapper .img-responsive-wrapper .img-responsive {
    height: 100%;
  }
  .it-carousel-wrapper.it-carousel-landscape-abstract .it-single-slide-wrapper .it-text-slider-wrapper-outside {
    flex-basis: 50%;
  }
  .it-carousel-wrapper.it-carousel-landscape-abstract .it-single-slide-wrapper .it-text-slider-wrapper-outside .card {
    margin-right: 0;
    border-right: none;
  }
  .it-carousel-wrapper.it-carousel-landscape-abstract .it-single-slide-wrapper .it-text-slider-wrapper-outside .card:after {
    display: none;
  }
  .it-carousel-wrapper.it-carousel-landscape-abstract .it-single-slide-wrapper .it-text-slider-wrapper-outside .card .card-body {
    padding-bottom: var(--bsi-carousel-landscape-card-spacing);
    padding-left: var(--bsi-carousel-landscape-card-spacing);
  }
  .it-carousel-wrapper.it-carousel-landscape-abstract .it-single-slide-wrapper .it-text-slider-wrapper-outside .card .card-body .card-signature {
    width: 60%;
    float: left;
  }
  .it-carousel-wrapper.it-carousel-landscape-abstract .it-single-slide-wrapper .it-text-slider-wrapper-outside .card .card-body a.read-more {
    position: inherit;
    bottom: inherit;
    justify-content: flex-end;
    width: 35%;
    margin-bottom: 0;
    float: right;
    line-height: 26px;
  }
  .it-carousel-wrapper.it-carousel-landscape-abstract-three-cols-arrow-visible .lined_slide:after {
    position: absolute;
    top: 0;
    right: calc(-1 * var(--bsi-carousel-divider-offset));
    bottom: 0;
    content: "";
    display: block;
    width: 1px;
    background-color: var(--bsi-carousel-divider-color);
  }
  .it-carousel-wrapper.it-carousel-landscape-abstract-3 {
    max-width: 960px;
  }
  .it-carousel-wrapper.it-carousel-landscape-abstract-3.it-full-carousel {
    max-width: unset;
  }
  .it-carousel-wrapper.it-carousel-landscape-abstract-three-cols-arrow-visible .splide__arrows {
    right: calc(-1 * var(--bsi-carousel-arrow-offset));
    left: calc(-1 * var(--bsi-carousel-arrow-offset));
  }
}
@media (min-width: 1200px) {
  .it-carousel-wrapper.it-carousel-landscape-abstract {
    max-width: 1176px;
  }
  .it-carousel-wrapper.it-carousel-landscape-abstract-3 {
    max-width: 1176px;
  }
  .it-carousel-wrapper.it-carousel-landscape-abstract-3.it-full-carousel {
    max-width: unset;
  }
}
@media (prefers-reduced-motion: reduce) {
  .it-carousel-wrapper .splide__pagination button {
    transition: none !important;
  }
}
*,
*::before,
*::after {
  box-sizing: border-box;
}

@media (prefers-reduced-motion: no-preference) {
  :root {
    scroll-behavior: smooth;
  }
}

body {
  margin: 0;
  -webkit-text-size-adjust: 100%;
  -webkit-tap-highlight-color: hsla(0, 0%, 0%, 0);
}

hr {
  margin: 1rem 0;
  color: inherit;
  border: 0;
  border-top: 1px solid;
  opacity: 0.25;
}

p {
  margin-top: 0;
  margin-bottom: var(--bsi-paragraph-spacing);
}

abbr[title] {
  text-decoration: underline dotted;
  cursor: help;
  text-decoration-skip-ink: none;
}

address {
  margin-bottom: var(--bsi-spacing-s);
  font-style: normal;
  line-height: inherit;
}

ol,
ul {
  padding-left: var(--bsi-spacing-l);
}

ol,
ul,
dl {
  margin-top: 0;
  margin-bottom: var(--bsi-spacing-s);
}

ol ol,
ul ul,
ol ul,
ul ol {
  margin-bottom: 0;
}

dt {
  font-weight: var(--bsi-font-weight-strong);
}

dd {
  margin-bottom: var(--bsi-spacing-xxs);
  margin-left: 0;
}

blockquote {
  margin: 0 0 var(--bsi-spacing-s);
}

sub,
sup {
  position: relative;
  font-size: var(--bsi-font-size-1);
  line-height: 0;
  vertical-align: baseline;
}

sub {
  bottom: -0.25em;
}

sup {
  top: -0.5em;
}

a {
  color: var(--bsi-color-link);
  text-decoration: underline;
  text-decoration-skip-ink: auto;
  text-underline-offset: 2px;
}
a:hover {
  color: var(--bsi-color-link-hover);
  cursor: pointer;
}

pre,
code,
kbd,
samp {
  font-size: 1em;
}

pre {
  display: block;
  margin-top: 0;
  margin-bottom: var(--bsi-paragraph-spacing);
  overflow: auto;
}
pre code {
  word-break: normal;
}

a > code {
  color: inherit;
}

figure {
  margin: 0 0 1rem;
}

img,
svg {
  vertical-align: middle;
}

table {
  caption-side: bottom;
  border-collapse: collapse;
}

caption {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  color: hsl(210, 17%, 44%);
  text-align: left;
}

th {
  text-align: inherit;
  text-align: -webkit-match-parent;
}

thead,
tbody,
tfoot,
tr,
td,
th {
  border-color: inherit;
  border-style: solid;
  border-width: 0;
}

label {
  display: inline-block;
}

button {
  border-radius: 0;
}

button:focus:not(:focus-visible) {
  outline: 0;
}

input,
button,
select,
optgroup,
textarea {
  margin: 0;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
}

button,
select {
  text-transform: none;
}

[role=button] {
  cursor: pointer;
}

select {
  word-wrap: normal;
}
select:disabled {
  opacity: 1;
}

[list]:not([type=date]):not([type=datetime-local]):not([type=month]):not([type=week]):not([type=time])::-webkit-calendar-picker-indicator {
  display: none !important;
}

button,
[type=button],
[type=reset],
[type=submit] {
  -webkit-appearance: button;
}
button:not(:disabled),
[type=button]:not(:disabled),
[type=reset]:not(:disabled),
[type=submit]:not(:disabled) {
  cursor: pointer;
}

::-moz-focus-inner {
  padding: 0;
  border-style: none;
}

textarea {
  resize: vertical;
}

fieldset {
  min-width: 0;
  padding: 0;
  margin: 0;
  border: 0;
}

::-webkit-datetime-edit-fields-wrapper,
::-webkit-datetime-edit-text,
::-webkit-datetime-edit-minute,
::-webkit-datetime-edit-hour-field,
::-webkit-datetime-edit-day-field,
::-webkit-datetime-edit-month-field,
::-webkit-datetime-edit-year-field {
  padding: 0;
}

::-webkit-inner-spin-button {
  height: auto;
}

[type=search] {
  outline-offset: -2px;
  -webkit-appearance: textfield;
}

/* rtl:raw:
[type="tel"],
[type="url"],
[type="email"],
[type="number"] {
  direction: ltr;
}
*/
::-webkit-search-decoration {
  -webkit-appearance: none;
}

::-webkit-color-swatch-wrapper {
  padding: 0;
}

::file-selector-button {
  font: inherit;
  -webkit-appearance: button;
}

output {
  display: inline-block;
}

iframe {
  border: 0;
}

summary {
  display: list-item;
  cursor: pointer;
}

progress {
  vertical-align: baseline;
}

[hidden] {
  display: none !important;
}

.visually-hidden,
.visually-hidden-focusable:not(:focus):not(:focus-within) {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

.img-responsive-wrapper {
  overflow: hidden;
}
.img-responsive-wrapper .img-responsive {
  position: relative;
  padding-bottom: 56.25%; /* 16:9 */
  height: 0;
}
.img-responsive-wrapper .img-responsive .img-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.img-responsive-wrapper .img-responsive .img-wrapper.custom-object-fit {
  background-size: cover;
  background-position: center;
  background-color: hsl(210, 6.2290689886%, 92.535%);
}
.img-responsive-wrapper .img-responsive .img-wrapper.custom-object-fit img {
  visibility: hidden;
}
.img-responsive-wrapper .img-responsive .img-wrapper img {
  width: 100%;
  display: block;
  height: 100%;
  object-fit: cover;
}

.container,
.container-fluid,
.container-xxl,
.container-xl,
.container-lg,
.container-md,
.container-sm {
  --bsi-gutter-x: 24px;
  --bsi-gutter-y: 0;
  width: 100%;
  padding-right: calc(var(--bsi-gutter-x) * 0.5);
  padding-left: calc(var(--bsi-gutter-x) * 0.5);
  margin-right: auto;
  margin-left: auto;
}
@media (min-width: 768px) {
  .container,
  .container-fluid,
  .container-xxl,
  .container-xl,
  .container-lg,
  .container-md,
  .container-sm {
    --bsi-gutter-x: 24px;
  }
}

@media (min-width: 576px) {
  .container-sm, .container {
    max-width: 540px;
  }
}
@media (min-width: 768px) {
  .container-md, .container-sm, .container {
    max-width: 720px;
  }
}
@media (min-width: 992px) {
  .container-lg, .container-md, .container-sm, .container {
    max-width: 960px;
  }
}
@media (min-width: 1200px) {
  .container-xl, .container-lg, .container-md, .container-sm, .container {
    max-width: 1176px;
  }
}
@media (min-width: 1400px) {
  .container-xxl, .container-xl, .container-lg, .container-md, .container-sm, .container {
    max-width: 1320px;
  }
}
it-card:not(:defined) {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: var(--bsi-spacing-s) var(--bsi-spacing-s);
  border: var(--bsi-border-base) var(--bsi-border-style) var(--bsi-border-color);
  column-gap: 1em;
}
it-card:not(:defined) [slot=image]:not(it-avatar):not(it-icon):not(.it-card-profile-image-icon-wrapper) {
  width: 100%;
  order: -1;
}
it-card:not(:defined) it-avatar[slot=image],
it-card:not(:defined) .it-card-profile-image-icon-wrapper {
  position: absolute;
  top: var(--bsi-spacing-s);
  right: var(--bsi-spacing-s);
}
it-card:not(:defined):not([ratio]) [slot=image] img, it-card:not(:defined)[ratio="16x9"] [slot=image] img {
  aspect-ratio: 16/9;
}
it-card:not(:defined)[ratio="21x9"] [slot=image] img {
  aspect-ratio: 21/9;
}
it-card:not(:defined)[ratio="4x3"] [slot=image] img {
  aspect-ratio: 4/3;
}
it-card:not(:defined)[ratio="1x1"] [slot=image] img {
  aspect-ratio: 1/1;
}
it-card:not(:defined)[variant=banner] it-icon[slot=image] {
  order: -1;
  margin-right: auto;
  margin-left: auto;
}
it-card:not(:defined)[variant^=inline-banner] * {
  margin-left: calc(var(--bsi-spacing-xl) + 2 * var(--bsi-spacing-s));
}
it-card:not(:defined)[variant^=inline-banner] it-icon[slot=image] {
  position: absolute;
  top: var(--bsi-spacing-s);
  left: var(--bsi-spacing-s);
  margin-left: 0;
}
it-card:not(:defined)[variant^=inline-banner][variant\$=reverse] * {
  margin-right: calc(var(--bsi-spacing-xl) + 2 * var(--bsi-spacing-s));
  margin-left: 0;
}
it-card:not(:defined)[variant^=inline-banner][variant\$=reverse] it-icon[slot=image] {
  right: var(--bsi-spacing-s);
  left: unset;
  margin-right: 0;
  margin-left: unset;
}
it-card:not(:defined) [slot=title] {
  display: flex;
  width: 100%;
  justify-content: space-between;
  margin-top: 0;
  margin-bottom: var(--bsi-heading-spacing);
  font-size: var(--bsi-heading-3-font-size);
  font-weight: var(--bsi-heading-font-weight);
  line-height: var(--bsi-heading-leading);
}
it-card:not(:defined) [slot=text],
it-card:not(:defined) [slot=body] {
  width: 100%;
}

it-card figure {
  margin-bottom: 0;
}
it-card img {
  border-top-left-radius: var(--bsi-card-border-radius-inner);
  border-top-right-radius: var(--bsi-card-border-radius-inner);
}
it-card a[slot=title]:focus-visible,
it-card a:focus-visible {
  display: inline-flex;
}
it-card .it-card-title-icon-wrapper {
  margin-left: var(--bsi-icon-spacing);
}
it-card .it-card-category {
  display: inline-block;
  margin-bottom: 0;
  color: var(--bsi-card-category-color);
  font-size: var(--bsi-card-category-font-size);
  font-weight: var(--bsi-card-category-font-weight);
  text-transform: uppercase;
}
it-card .it-card-chips {
  display: flex;
  flex-wrap: wrap;
  padding: 0;
  margin-bottom: 0;
  gap: calc(var(--bsi-card-spacing-y) * 0.5);
  list-style-type: none;
}
it-card .it-card-date {
  color: var(--bsi-card-date-color);
  font-size: var(--bsi-card-date-font-size);
}
it-card .it-card-link {
  color: var(--bsi-card-link-color);
}
it-card .it-card-link:hover {
  color: rgb(47.29536, 64.26, 81.22464);
}
it-card .it-card-link + .it-card-link {
  margin-left: var(--bsi-card-spacing-x);
}
it-card .it-card-description-list {
  display: flex;
  flex-direction: column;
  padding-top: var(--bsi-card-spacing-y);
  padding-bottom: var(--bsi-card-spacing-y);
  border-top: var(--bsi-card-border-separator-width) solid var(--bsi-card-border-color);
  border-bottom: var(--bsi-card-border-separator-width) solid var(--bsi-card-border-color);
  color: var(--bsi-card-description-color);
  font-size: var(--bsi-label-font-size);
  gap: var(--bsi-spacing-s);
  line-height: var(--bsi-body-leading);
}
@media (max-width: 575.98px) {
  it-card .it-card-description-list {
    display: flex;
    flex-direction: column;
  }
}
it-card .it-card-description-list dt {
  display: inline;
  margin-right: 0.125rem;
  font-weight: bold;
}
it-card .it-card-description-list dd {
  display: inline-block;
  margin: 0;
}
it-card .it-card-profile-image-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--bsi-color-background-primary-lighter);
}
it-card .list-group {
  border-top: var(--bsi-card-border-separator-width) solid var(--bsi-card-border-color);
  border-bottom: var(--bsi-card-border-separator-width) solid var(--bsi-card-border-color);
  margin-top: var(--bsi-spacing-s);
  margin-bottom: var(--bsi-spacing-s);
}
it-card .list-group .list-group-item {
  padding: var(--bsi-spacing-xs) 0;
}
it-card .list-group:first-child {
  border-top-width: 0;
  border-top-left-radius: var(--bsi-card-border-radius-inner);
  border-top-right-radius: var(--bsi-card-border-radius-inner);
}
it-card .list-group:last-child {
  border-bottom-width: 0;
  border-bottom-right-radius: var(--bsi-card-border-radius-inner);
  border-bottom-left-radius: var(--bsi-card-border-radius-inner);
}

ul.it-card-list {
  padding: 0;
  margin: 0;
  list-style-type: none;
}
ul.it-card-list > li.col,
ul.it-card-list > li[class*=col-] {
  display: block;
  border: none;
  margin-top: 0;
  background: transparent;
  transition: none;
}

.it-card-group {
  --bsi-card-group-gap: 24px;
  display: flex;
  flex-wrap: wrap;
  gap: var(--bsi-card-group-gap);
}
.it-card-group > it-card {
  margin: 0;
}
@supports (container-type: inline-size) {
  .it-card-group.it-card-group-container-aware {
    container-type: inline-size;
  }
  .it-card-group.it-card-group-container-aware > it-card {
    flex: 1 0 100%;
  }
  @media (min-width: 576px) {
    .it-card-group.it-card-group-container-aware > it-card {
      flex: auto;
    }
  }
  @container (min-width: 500px) {
    .it-card-group.it-card-group-container-aware > it-card {
      flex: 0 0 calc(50% - 0.5 * var(--bsi-card-group-gap));
    }
  }
  @container (min-width: 800px) {
    .it-card-group.it-card-group-container-aware > it-card {
      flex: 0 0 calc(33.333% - 0.667 * var(--bsi-card-group-gap));
    }
  }
  @container (min-width: 1100px) {
    .it-card-group.it-card-group-container-aware > it-card {
      flex: 0 0 calc(25% - 0.75 * var(--bsi-card-group-gap));
    }
  }
  @container (min-width: 500px) {
    .it-card-group.it-card-group-container-aware.it-card-group-2-cols > it-card {
      flex: 0 0 calc(50% - 0.5 * var(--bsi-card-group-gap));
    }
  }
  @container (min-width: 800px) {
    .it-card-group.it-card-group-container-aware.it-card-group-3-cols > it-card {
      flex: 0 0 calc(33.333% - 0.667 * var(--bsi-card-group-gap));
    }
  }
}
.it-card-group {
  /* stylelint-disable-next-line no-duplicate-selectors */
}
.it-card-group > it-card {
  flex: 0 0 calc(100% - var(--bsi-card-group-gap));
}
@media (min-width: 576px) {
  .it-card-group > it-card {
    flex: 0 0 calc(50% - 0.5 * var(--bsi-card-group-gap));
  }
}
@media (min-width: 768px) {
  .it-card-group > it-card {
    flex: 0 0 calc(33.333% - 0.667 * var(--bsi-card-group-gap));
  }
}
@media (min-width: 992px) {
  .it-card-group > it-card {
    flex: 0 0 calc(25% - 0.75 * var(--bsi-card-group-gap));
  }
}
@media (min-width: 576px) {
  .it-card-group.it-card-group-2-cols > it-card, .it-card-group.it-card-group-3-cols > it-card, .it-card-group.it-card-group-4-cols > it-card {
    flex: 0 0 calc(50% - 0.5 * var(--bsi-card-group-gap));
  }
}
@media (min-width: 768px) {
  .it-card-group.it-card-group-3-cols > it-card {
    flex: 0 0 calc(33.333% - 0.667 * var(--bsi-card-group-gap));
  }
}
@media (min-width: 768px) {
  .it-card-group.it-card-group-4-cols > it-card {
    flex: 0 0 calc(33.333% - 0.667 * var(--bsi-card-group-gap));
  }
}
@media (min-width: 992px) {
  .it-card-group.it-card-group-4-cols > it-card {
    flex: 0 0 calc(25% - 0.75 * var(--bsi-card-group-gap));
  }
}
.it-card-group.it-card-group-equal-height > it-card {
  height: 100%;
}
.it-card-group.it-card-group-center {
  justify-content: center;
}
.it-card-group.it-card-group-end {
  justify-content: flex-end;
}

.border-primary {
  --bsi-border-color: var(--bsi-color-border-primary);
}

.border-secondary {
  --bsi-border-color: var(--bsi-color-border-secondary);
}

.border-success {
  --bsi-border-color: var(--bsi-color-border-success);
}

.border-warning {
  --bsi-border-color: var(--bsi-color-border-warning);
}

.border-danger {
  --bsi-border-color: var(--bsi-color-border-danger);
}

.border-inverse {
  --bsi-border-color: var(--bsi-color-border-inverse);
}

.border-white {
  --bsi-border-color: var(--bsi-color-border-inverse);
}

:focus-visible,
button:focus-visible,
::part(focusable):focus-visible,
.it-carousel-wrapper .splide__pagination button:focus-visible,
.upload:focus-visible + label,
.upload-dragdrop-input:focus-visible[type=file] + label,
.avatar-upload:focus-within:focus-visible,
.rating input:focus-visible + label svg {
  border-color: hsl(0, 0%, 0%) !important;
  box-shadow: 0 0 0 2px var(--bsi-color-border-inverse), 0 0 0 5px var(--bsi-color-outline-focus) !important;
  outline: 3px solid transparent !important;
  outline-offset: 3px !important;
}

[tabindex="-1"]:focus:not(:focus-visible) {
  box-shadow: none !important;
}

:focus:not(:focus-visible):not(.btn),
::part(focusable):focus-visible:not(.btn) {
  border-color: inherit !important;
  box-shadow: none !important;
  outline: none !important;
}

p a:focus:not(:focus-visible):not(.btn) {
  /* focus da mouse o script, esclusi anche i .btn */
  background: hsl(0, 0%, 100%);
  box-shadow: none !important;
  color: var(--bsi-color-link) !important;
  outline: 3px hsl(0, 0%, 0%) solid !important;
  outline-offset: 0px !important;
}

it-icon {
  line-height: 0;
}

:host {
  display: block;
}

it-carousel-slide {
  width: 100%;
}

.splide__slide img {
  width: 100%;
  vertical-align: bottom;
}

:host([arrows]) .it-carousel-wrapper:not(.it-carousel-landscape-abstract-three-cols-arrow-visible) .splide__track {
  position: relative;
}
:host([arrows]) .it-carousel-wrapper:not(.it-carousel-landscape-abstract-three-cols-arrow-visible) .splide__arrows {
  position: absolute;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  inset-block-start: 50%;
  inset-inline-end: calc(8px * -3);
  inset-inline-start: calc(8px * -3);
  pointer-events: none;
  translate: 0 -50%;
}
:host([arrows]) .it-carousel-wrapper:not(.it-carousel-landscape-abstract-three-cols-arrow-visible) .splide__arrow {
  display: grid;
  width: calc(8px * 5);
  height: calc(8px * 5);
  padding: 0;
  border: none;
  border-radius: 0;
  background-color: var(--bsi-color-text-primary);
  cursor: pointer;
  place-items: center;
  pointer-events: all;
  transition: background-color 0.2s ease;
}
:host([arrows]) .it-carousel-wrapper:not(.it-carousel-landscape-abstract-three-cols-arrow-visible) .splide__arrow svg {
  display: block;
  width: calc(8px * 2);
  height: calc(8px * 2);
  fill: var(--bsi-color-text-inverse, #fff);
}
@media (hover: hover) {
  :host([arrows]) .it-carousel-wrapper:not(.it-carousel-landscape-abstract-three-cols-arrow-visible) .splide__arrow:hover:not([disabled]) {
    background-color: color-mix(in srgb, var(--bsi-color-text-primary) 85%, black);
  }
}
:host([arrows]) .it-carousel-wrapper:not(.it-carousel-landscape-abstract-three-cols-arrow-visible) .splide__arrow:focus-visible {
  outline: 2px solid var(--bsi-color-outline-focus);
  outline-offset: 2px;
}
:host([arrows]) .it-carousel-wrapper:not(.it-carousel-landscape-abstract-three-cols-arrow-visible) .splide__arrow[disabled] {
  background-color: hsl(210, 3%, 85%);
  cursor: not-allowed;
}
@media (min-width: 992px) {
  :host([arrows]) .it-carousel-wrapper:not(.it-carousel-landscape-abstract-three-cols-arrow-visible) .splide__arrows {
    inset-inline-end: calc(8px * -6);
    inset-inline-start: calc(8px * -6);
  }
}

@media (prefers-reduced-motion: reduce) {
  :host([arrows]) .it-carousel-wrapper:not(.it-carousel-landscape-abstract-three-cols-arrow-visible) .splide__arrow {
    transition: none;
  }
}
.splide__arrows {
  position: absolute;
  z-index: 1;
  top: 50%;
  right: -48px;
  left: -48px;
  display: flex;
  justify-content: space-between;
  pointer-events: none;
}
@media (max-width: 767.98px) {
  .splide__arrows {
    padding-inline: 1rem;
  }
}
.splide__arrows .splide__arrow {
  display: flex;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  border: none;
  background: var(--bsi-color-text-primary) !important;
  pointer-events: all;
}
.splide__arrows .splide__arrow svg {
  width: 16px;
  height: 16px;
  fill: var(--bsi-color-text-inverse, #fff);
}
.splide__arrows .splide__arrow.splide__arrow--prev {
  transform: rotate(180deg);
}
.splide__arrows .splide__arrow[disabled] {
  background: hsl(210, 3%, 85%) !important;
}

.splide__controls {
  display: flex;
  align-items: baseline;
  justify-content: center;
  margin-top: 1rem;
  gap: 1rem;
}

.splide__toggle {
  display: flex;
  width: 1.8rem;
  height: 1.8rem;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-color: transparent;
  border-radius: 50%;
  margin: 0;
  background: var(--bsi-color-text-primary) !important;
  color: var(--bsi-color-text-inverse) !important;
  cursor: pointer;
  outline: none;
  transition: background-color 0.2s ease;
}
.splide__toggle svg {
  width: 1rem;
  height: 1rem;
  margin-left: 4px;
  fill: var(--bsi-color-text-inverse) !important;
}
.splide__toggle.is-active svg {
  margin-left: 0px;
}

.it-carousel-wrapper .it-header-block ::slotted(h2),
.it-carousel-wrapper .it-header-block ::slotted(h3),
.it-carousel-wrapper .it-header-block ::slotted(h4),
.it-carousel-wrapper .it-header-block ::slotted(h5),
.it-carousel-wrapper .it-header-block ::slotted(h6) {
  padding-bottom: var(--bsi-carousel-pagination-spacing);
  border-bottom: 1px solid var(--bsi-carousel-heading-border-color);
  margin-bottom: 0;
}

:host([variant=gallery-sm]) .splide__pagination,
:host([variant=gallery-lg]) .splide__pagination {
  padding-top: 0;
}

.splide__pagination {
  padding-top: 0.5rem;
}
.splide__pagination button {
  display: inline-block;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  border-radius: 50px !important;
  background: var(--bsi-color-background-subtle) !important;
  box-shadow: none;
  transition: all 0.3s;
}
.splide__pagination button.is-active {
  background: var(--bsi-color-text-primary) !important;
}

:host(:not([variant=gallery-sm]):not([variant=gallery-lg])) .splide__list .splide__slide {
  display: flex;
  justify-content: center;
}

@media (min-width: 992px) {
  .lined_slide:after {
    position: absolute;
    top: 0;
    right: -12px;
    bottom: 0;
    display: block;
    width: 1px;
    background-color: hsl(210, 32.2033898305%, 92.625%);
    content: "";
  }
}
.figure.img-full img {
  width: 100%;
  height: 100%;
  max-height: 600px;
  object-fit: cover;
}`;

export { BaseComponent as B, __decorate as _, __metadata as a, BaseLocalizedComponent as b, registerTranslation as r, styles as s };
//# sourceMappingURL=carousel-CG7LrhJn.js.map
